import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, X, Info } from "lucide-react";

/**
 * Consent capture modal — appears once after login if the user has any
 * REQUIRED consent that's un-granted or on an outdated version. Grants +
 * revocations are all captured with IP + user-agent server-side (see
 * /app/backend/core/security/consents.py).
 *
 * The optional consents (marketing, third-party, research) are shown as
 * toggle-able but not blocking — the user can dismiss with any decision.
 */
export default function ConsentGate({ onDone }) {
  const [pending, setPending] = useState(null);   // null = still loading
  const [catalog, setCatalog] = useState({});     // consent_type -> catalog meta
  const [choices, setChoices] = useState({});     // consent_type -> boolean
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [me, cat] = await Promise.all([
        api.get("/consents/mine"),
        api.get("/consents/catalog"),
      ]);
      const catMap = {};
      (cat.data.consents || []).forEach(c => { catMap[c.type] = c; });
      setCatalog(catMap);

      const optionals = (cat.data.consents || []).filter(c => !c.required);
      const initial = {};
      // Required consents default to un-checked (user must actively opt in)
      me.data.pending.forEach(p => { initial[p.type] = false; });
      // Optional consents inherit their latest recorded value (or false if never decided)
      optionals.forEach(o => {
        initial[o.type] = (me.data.latest?.[o.type]?.status === "granted");
      });
      setChoices(initial);
      setPending(me.data.pending || []);
      if ((me.data.pending || []).length === 0) {
        // Nothing required outstanding — quietly close
        onDone?.();
      }
    } catch {
      onDone?.();  // fail-open: never block the user
    }
  }, [onDone]);

  useEffect(() => { load(); }, [load]);

  if (pending === null) return null;               // still loading — no flash
  if (pending.length === 0) return null;           // nothing to prompt for

  // All required consents must be ticked before Continue is enabled
  const allRequiredGranted = pending.every(p => choices[p.type]);

  async function submit() {
    setSaving(true);
    try {
      const grants = Object.entries(choices).filter(([, v]) => v).map(([k]) => k);
      const revokes = Object.entries(choices).filter(([, v]) => !v).map(([k]) => k);
      await api.post("/consents/decide", { grants, revokes });
      toast.success("Preferences saved");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed — please try again");
    } finally {
      setSaving(false);
    }
  }

  // All consent types the user hasn't answered yet — required first, then optional
  const allTypes = Object.values(catalog).sort((a, b) => (b.required ? 1 : 0) - (a.required ? 1 : 0));

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="consent-modal">
      <div className="bg-[#0F1312] border border-[#272E2B] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-5 border-b border-[#272E2B] flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[#4ADE80]/10 border border-[#4ADE80]/30 flex items-center justify-center">
            <ShieldCheck size={20} className="text-[#4ADE80]" />
          </div>
          <div>
            <h2 className="font-heading text-lg text-white">Your privacy choices</h2>
            <p className="text-xs text-[#A3ACA7] mt-0.5">
              We need your consent before we handle any of your health data.
              You can change these anytime under Security.
            </p>
          </div>
        </div>

        <div className="p-6 space-y-3 overflow-y-auto">
          {allTypes.map((c) => {
            const checked = !!choices[c.type];
            return (
              <label key={c.type}
                     data-testid={`consent-row-${c.type}`}
                     className="flex items-start gap-3 rounded-xl border border-[#272E2B] bg-[#1C201E]/50 p-4 cursor-pointer hover:border-[#4ADE80]/30 transition-colors">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setChoices(prev => ({ ...prev, [c.type]: e.target.checked }))}
                  className="mt-1 w-4 h-4 accent-[#4ADE80] shrink-0"
                  data-testid={`consent-checkbox-${c.type}`}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white">{c.label}</span>
                    {c.required ? (
                      <span className="text-[9px] font-bold text-[#F87171] px-1.5 py-0.5 rounded-full bg-[#F87171]/10">REQUIRED</span>
                    ) : (
                      <span className="text-[9px] font-bold text-[#A3ACA7] px-1.5 py-0.5 rounded-full bg-[#A3ACA7]/10">OPTIONAL</span>
                    )}
                    <span className="text-[10px] text-[#7A8480]">v{c.version}</span>
                  </div>
                  <p className="text-xs text-[#A3ACA7] mt-1.5 leading-relaxed">{c.description}</p>
                </div>
              </label>
            );
          })}

          <div className="pt-2 flex items-start gap-2 text-[11px] text-[#7A8480]">
            <Info size={12} className="shrink-0 mt-0.5" />
            <span>
              Your IP and browser are stored alongside every consent decision so
              we can prove compliance during an audit. Revoke anytime under
              Security → Privacy choices. See our full privacy notice for details.
            </span>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#272E2B] flex items-center justify-between gap-2">
          <div className="text-[11px] text-[#A3ACA7]">
            {allRequiredGranted
              ? "All required consents ticked."
              : `${pending.length - pending.filter(p => choices[p.type]).length} required consent(s) still needed.`}
          </div>
          <button
            onClick={submit}
            disabled={!allRequiredGranted || saving}
            data-testid="consent-submit-btn"
            className="text-sm bg-[#4ADE80] hover:bg-[#22C55E] disabled:opacity-40 text-black font-semibold rounded-lg px-5 py-2 flex items-center gap-1.5 transition-colors"
          >
            <CheckCircle2 size={14} />
            {saving ? "Saving…" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
